import { useEffect, useState, type ReactNode } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { PPAccountIntegration } from "../../types/paymentProcessor";

const LOOP_REQUIRED_SCOPES = ['Subscription Contracts', 'Orders'];

const LOOP_TOKEN_GUIDE_URL = 'https://help.loopwork.co/en/articles/12733109-generate-loop-api-tokens';

const KACHING_TOKEN_GUIDE_URL = 'https://developers.kachingappz.app/subscriptions/getting-started/';

const LOOP_INSTRUCTIONS_TEXT = [
  'How to create the Loop API token',
  '',
  '1. In Loop, go to Settings → Manage API tokens',
  '2. Click Generate Token',
  '3. Name: Chargemont',
  '4. Scopes — set each of these to "Read & write":',
  ...LOOP_REQUIRED_SCOPES.map((scope) => `     - ${scope}`),
  '5. Click Generate, then click "Show token" and copy it',
  '',
  `Loop guide: ${LOOP_TOKEN_GUIDE_URL}`,
  '',
  '6. Paste the token into Chargemont.',
].join('\n');

const KACHING_INSTRUCTIONS_TEXT = [
  'How to create the Kaching API token',
  '',
  '1. Generate an API token in the Kaching Subscriptions app admin (the Advanced plan or higher is required)',
  '2. Copy the token',
  '',
  `Kaching guide: ${KACHING_TOKEN_GUIDE_URL}`,
  '',
  '3. Paste the token into Chargemont.',
].join('\n');

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.85em',
        backgroundColor: '#ececf0',
        px: 0.5,
        py: 0.1,
        borderRadius: 0.5,
      }}
    >
      {children}
    </Box>
  );
}

export type SubscriptionApp = 'loop' | 'kaching';

interface AppCopy {
  name: string;
  inputLabel: string;
  guideUrl: string;
  guideLinkText: string;
  instructionsTitle: string;
  instructions: ReactNode;
  instructionsText: string;
  displayToken: (account: PPAccountIntegration) => string | null | undefined;
  cancelModeActive: (account: PPAccountIntegration) => boolean;
  enableCancellationLabel: string;
}

const COPY: Record<SubscriptionApp, AppCopy> = {
  loop: {
    name: 'Loop',
    inputLabel: 'Loop admin API token',
    guideUrl: LOOP_TOKEN_GUIDE_URL,
    guideLinkText: "Loop's guide to generating API tokens",
    instructionsTitle: 'How to create the Loop token',
    instructionsText: LOOP_INSTRUCTIONS_TEXT,
    displayToken: (a) => a.displayLoopToken,
    cancelModeActive: (a) => !!a.loopCancellationEnabled && !a.loopCancellationDryRun,
    enableCancellationLabel: 'Cancel Loop subscriptions',
    instructions: (
      <>
        <li>
          In Loop, go to <strong>Settings → Manage API tokens</strong>
        </li>
        <li>
          Click <strong>Generate Token</strong>
        </li>
        <li>
          <strong>Name:</strong> <Mono>Chargemont</Mono>
        </li>
        <li>
          <strong>Scopes</strong> — set each of these to <strong>Read &amp; write</strong>:
          <Box component="ul">
            {LOOP_REQUIRED_SCOPES.map((scope) => (
              <li key={scope}>{scope}</li>
            ))}
          </Box>
        </li>
        <li>
          Click <strong>Generate</strong>, then click <strong>Show token</strong> and copy it
        </li>
      </>
    ),
  },
  kaching: {
    name: 'Kaching',
    inputLabel: 'Kaching API token',
    guideUrl: KACHING_TOKEN_GUIDE_URL,
    guideLinkText: "Kaching's developer docs",
    instructionsTitle: 'How to create the Kaching token',
    instructionsText: KACHING_INSTRUCTIONS_TEXT,
    displayToken: (a) => a.displayKachingToken,
    cancelModeActive: (a) =>
      (!!a.kachingCancellationEnabled && !a.kachingCancellationDryRun)
      || (!!a.kachingCancellationOnChargebackEnabled && !a.kachingCancellationOnChargebackDryRun),
    enableCancellationLabel: 'Cancel Kaching subscriptions',
    instructions: (
      <>
        <li>
          Generate an API token in the <strong>Kaching Subscriptions</strong> app admin (the{' '}
          <strong>Advanced</strong> plan or higher is required)
        </li>
        <li>Copy the token</li>
      </>
    ),
  },
};

interface SubscriptionAppTokenDialogProps {
  open: boolean;
  account: PPAccountIntegration | null;
  app: SubscriptionApp;
  onClose: () => void;
  onSave: (token: string | null, enableCancellation: boolean) => Promise<void>;
}

type Mode = 'idle' | 'edit' | 'clear';

export default function SubscriptionAppTokenDialog({ open, account, app, onClose, onSave }: SubscriptionAppTokenDialogProps) {
  const copy = COPY[app];
  const [mode, setMode] = useState<Mode>('idle');
  const [tokenInput, setTokenInput] = useState('');
  const [enableCancellation, setEnableCancellation] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const displayToken = account ? copy.displayToken(account) : null;

  const handleCopyInstructions = async () => {
    await navigator.clipboard.writeText(copy.instructionsText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (open) {
      setMode(displayToken ? 'idle' : 'edit');
      setTokenInput('');
      setEnableCancellation(true);
      setError(null);
      setSubmitting(false);
      setCopied(false);
    }
  }, [open, displayToken]);

  if (!account) return null;

  const hasToken = !!displayToken;
  const cancelModeActive = copy.cancelModeActive(account);

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleSaveNewToken = async () => {
    const trimmed = tokenInput.trim();
    if (!trimmed) return;
    setError(null);
    setSubmitting(true);
    try {
      await onSave(trimmed, !hasToken && enableCancellation);
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to save ${copy.name} token`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmClear = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onSave(null, false);
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to clear ${copy.name} token`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>{copy.name} Token — {account.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {mode === 'edit' && (
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              overflow: 'hidden',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: '#f5f5f7', '& .MuiAccordionSummary-content': { my: 1 } }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  pr: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {copy.instructionsTitle}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    copied ? (
                      <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyInstructions();
                  }}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {copied ? 'Copied!' : 'Copy Instructions'}
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1 }}>
              <Box
                sx={{
                  color: 'text.secondary',
                  fontSize: 14,
                  lineHeight: 1.6,
                  '& strong': { color: 'text.primary' },
                  '& ul': { mt: 0.5, mb: 0, pl: 2.5 },
                  '& ul > li': { mb: 0.25 },
                }}
              >
                <Box component="ol" sx={{ m: 0, pl: 2.5, '& > li': { mb: 1 } }}>
                  {copy.instructions}
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Link href={copy.guideUrl} target="_blank" rel="noopener noreferrer">
                    {copy.guideLinkText}
                  </Link>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Current token
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace' }}
              color={hasToken ? 'text.primary' : 'text.secondary'}
            >
              {displayToken || '— Not set'}
            </Typography>
          </Box>

          {mode === 'edit' && (
            <TextField
              label={hasToken ? 'New token' : copy.inputLabel}
              size="small"
              type="password"
              fullWidth
              autoComplete="new-password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          )}

          {mode === 'edit' && !hasToken && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableCancellation}
                  onChange={(e) => setEnableCancellation(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={copy.enableCancellationLabel}
            />
          )}

          {mode === 'clear' && (
            <Alert severity="warning">
              Clearing the token will also disable {copy.name} cancel mode
              {cancelModeActive ? ' (currently set to "On (cancel)")' : ''}.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {mode === 'idle' && (
          <>
            <Button onClick={onClose} disabled={submitting}>Close</Button>
            {hasToken && (
              <Button color="error" onClick={() => setMode('clear')} disabled={submitting}>
                Clear
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => setMode('edit')}
              disabled={submitting}
              sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
            >
              {hasToken ? 'Replace' : 'Add token'}
            </Button>
          </>
        )}
        {mode === 'edit' && (
          <>
            <Button
              onClick={() => {
                if (hasToken) { setMode('idle'); setTokenInput(''); }
                else { onClose(); }
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveNewToken}
              disabled={submitting || !tokenInput.trim()}
              sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
            >
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
        {mode === 'clear' && (
          <>
            <Button onClick={() => setMode('idle')} disabled={submitting}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmClear} disabled={submitting}>
              {submitting ? 'Clearing…' : 'Confirm clear'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
